/**
 * The content column of a `WorkshopList` card — everything to the right of
 * the date block. Split out of `WorkshopListItem` purely to keep that file's
 * render function under the repo's line cap; it owns no state or logic of its
 * own, just the resolved `visibility` it's handed.
 *
 * A caller's `right` content is raised above the row's open button so its
 * controls stay clickable, and shrink-wrapped so it raises no more than that
 * — a full-width wrapper would sit over the button along the whole row and
 * swallow every click in the empty space beside the control.
 */
import type { ReactNode } from 'react';

import { WorkshopListHeader } from './WorkshopListHeader';
import { WorkshopListMeta } from './WorkshopListMeta';
import { WorkshopListSalesRow } from './WorkshopListSalesRow';
import type { WorkshopListSize } from './workshopListSizes';
import { WorkshopListStaffSealRow } from './WorkshopListStaffSealRow';
import type { WorkshopListVisibility } from './workshopListVisibility';
import type { WorkshopChip } from './workshopsCalendarTypes';

export interface WorkshopListContentProps {
  w: WorkshopChip;
  timeLabel: string;
  visibility: WorkshopListVisibility;
  right?: (w: WorkshopChip) => ReactNode;
  size?: WorkshopListSize;
}

export function WorkshopListContent({
  w,
  timeLabel,
  visibility,
  right,
  size = 'md',
}: WorkshopListContentProps) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        padding: '2px 2px 2px 0',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <WorkshopListHeader
        venueName={w.venueName}
        showVenue={visibility.venue}
        time={timeLabel}
        showTime={visibility.time}
        size={size}
      />
      <WorkshopListMeta
        subregionLabel={w.subregionLabel}
        species={w.species}
        color={w.accentColor}
        showSubregion={visibility.subregion}
        showSpecies={visibility.species}
        size={size}
      />
      <WorkshopListStaffSealRow
        teacher={w.leadInstructor}
        assistant={w.assistant}
        showTeacher={visibility.teacherLine}
        showAssistant={visibility.assistantLine}
        replacements={w.replacements}
        showSeal={visibility.seal}
        size={size}
      />
      {right && (
        <div
          data-testid="workshop-list-right"
          style={{
            position: 'relative',
            zIndex: 2,
            alignSelf: 'flex-end',
            marginTop: 7,
            display: 'flex',
          }}
        >
          {right(w)}
        </div>
      )}
      <WorkshopListSalesRow
        show={visibility.salesRow}
        booked={w.booked}
        capacity={w.capacity}
        showBar={visibility.bar}
        showCount={visibility.count}
        status={w.status}
        showPacing={visibility.pacing}
        size={size}
      />
    </div>
  );
}
